import { MoreHorizontal, Plus } from 'lucide-react';
import type { OrganizationWebhookDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sara/ui/components/dropdown-menu';
import { Switch } from '@sara/ui/components/switch';
import { cn } from '@sara/ui/lib/utils';

export const WEBHOOK_CAP = 10;

interface WebhookListProps {
  webhooks: OrganizationWebhookDto[];
  onToggleEnabled: (webhook: OrganizationWebhookDto, nextEnabled: boolean) => void;
  onEdit: (webhook: OrganizationWebhookDto) => void;
  onDelete: (webhook: OrganizationWebhookDto) => void;
  pendingToggleIds: ReadonlySet<string>;
  onCreate: () => void;
  cap?: number;
}

function formatDate(value: string | Date) {
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const COLUMNS = 'grid-cols-[1.4fr_1fr_1fr_7rem]';
const COLUMN_TEMPLATE = '1.4fr 1fr 1fr 7rem';

export function WebhookList({
  webhooks,
  onToggleEnabled,
  onEdit,
  onDelete,
  pendingToggleIds,
  onCreate,
  cap = WEBHOOK_CAP,
}: WebhookListProps) {
  const atCap = webhooks.length >= cap;

  if (webhooks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">
          No webhook receivers yet.
        </p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Add a receiver to start getting signed event deliveries from AI Interviewer.
        </p>
        <Button variant="tinted" shape="pill" size="sm" className="mt-6 gap-1.5" onClick={onCreate}>
          <Plus className="h-4 w-4" strokeWidth={1.6} />
          Add webhook
        </Button>
      </div>
    );
  }

  const sorted = [...webhooks].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <DataPanel>
      <DataPanel.Header columns={COLUMN_TEMPLATE}>
        <span>Receiver</span>
        <span>Status</span>
        <span>Created</span>
        <span className="sr-only">Actions</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {sorted.map((webhook) => {
            const isTogglePending = pendingToggleIds.has(webhook.id);
            const displayLabel = webhook.label ?? hostOf(webhook.url);
            return (
              <li
                key={webhook.id}
                className={cn(
                  'grid items-center gap-6 px-6 py-5',
                  COLUMNS,
                  !webhook.enabled && 'opacity-70'
                )}
              >
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-medium tracking-tight text-foreground">
                    {displayLabel}
                  </p>
                  <p className="mt-1 truncate font-mono text-[12.5px] text-muted-foreground">
                    {webhook.url}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    <code className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground/80">
                      {webhook.secretPrefix}…
                    </code>
                  </div>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 text-[12px] font-medium',
                    webhook.enabled ? 'text-primary' : 'text-muted-foreground/80'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'h-1 w-1 rounded-full',
                      webhook.enabled ? 'bg-primary' : 'bg-muted-foreground/50'
                    )}
                  />
                  {webhook.enabled ? 'Active' : 'Paused'}
                </span>
                <span className="text-[13px] tabular-nums text-muted-foreground">
                  {formatDate(webhook.createdAt)}
                </span>
                <div className="flex shrink-0 items-center gap-3 justify-self-end">
                  <Switch
                    checked={webhook.enabled}
                    disabled={isTogglePending}
                    onCheckedChange={(next) => onToggleEnabled(webhook, next)}
                    aria-label={webhook.enabled ? 'Disable webhook' : 'Enable webhook'}
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full text-muted-foreground hover:text-foreground"
                        aria-label="Webhook actions"
                      >
                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.6} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => onEdit(webhook)}>Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => onDelete(webhook)}
                        className="text-destructive focus:text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            );
          })}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">
          {webhooks.length} of {cap} webhooks
        </span>
        {atCap && (
          <span className="text-amber-700 dark:text-amber-400">
            At the per-org cap. Delete one to add another.
          </span>
        )}
      </DataPanel.Footer>
    </DataPanel>
  );
}
